<template>
    <div class="campaign-flow">
        <header class="campaign-flow__header">
            <div class="campaign-flow__logo">2digital DOOH</div>
            <h1 class="campaign-flow__title">Как запустить кампанию</h1>
            <p class="campaign-flow__subtitle">
                Пошаговый флоу от подготовки креативов до получения отчётов.
            </p>
            <div class="campaign-flow__actions">
                <button
                    type="button"
                    class="campaign-flow__btn campaign-flow__btn--primary"
                    :disabled="isDismissing"
                    @click="dismissFlow"
                >
                    {{ isDismissing ? 'Переход…' : 'Перейти к кампаниям' }}
                </button>
                <button
                    type="button"
                    class="campaign-flow__btn campaign-flow__btn--ghost"
                    :disabled="isDismissing"
                    @click="dismissFlow"
                >
                    Больше не показывать
                </button>
            </div>
            <p v-if="dismissError" class="campaign-flow__error">{{ dismissError }}</p>
        </header>

        <div class="campaign-flow__flow">
            <!-- STEP 1 -->
            <div class="step-wrapper">
                <div class="step step-1" :class="{ open: openSteps.step1 }">
                    <div class="step-header" @click="toggleStep('step1')">
                        <div class="step-num">1</div>
                        <div class="step-label">
                            <strong>Креативы</strong>
                            <span>Подготовка материалов для размещения</span>
                        </div>
                        <svg class="chevron" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 7.5l5 5 5-5"/></svg>
                    </div>
                    <div class="step-body">
                        <div class="specs-block">
                            <div class="specs-title">Технические требования к материалам</div>
                            <div class="specs-note">
                                Основаны на стандартах JCDecaux / общих DOOH-нормах. Финальные параметры уточняйте у оператора перед сдачей.
                            </div>

                            <table class="specs">
                                <thead>
                                    <tr>
                                        <th>Формат экрана</th>
                                        <th>Разрешение</th>
                                        <th>Ориентация</th>
                                        <th>Хронометраж</th>
                                        <th>Тип файла</th>
                                        <th>Макс. вес</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>
                                            Суперсайт / Билборд
                                            <br><small>6×3 м и аналоги</small>
                                        </td>
                                        <td>1920×1080 px</td>
                                        <td><span class="badge badge-h">Горизонт.</span></td>
                                        <td>5 / 10 / 15 сек</td>
                                        <td>MP4, JPEG</td>
                                        <td>100 МБ</td>
                                    </tr>
                                    <tr>
                                        <td>
                                            Сити-лайт / Пилон
                                            <br><small>1,2×1,8 м и аналоги</small>
                                        </td>
                                        <td>1080×1920 px</td>
                                        <td><span class="badge badge-v">Вертикаль.</span></td>
                                        <td>5 / 10 сек</td>
                                        <td>MP4, JPEG</td>
                                        <td>100 МБ</td>
                                    </tr>
                                    <tr>
                                        <td>
                                            LED-экран (улица)
                                            <br><small>нестандартные пропорции</small>
                                        </td>
                                        <td>Уточнять у оператора</td>
                                        <td>—</td>
                                        <td>10 сек</td>
                                        <td>MP4</td>
                                        <td>20 МБ</td>
                                    </tr>
                                </tbody>
                            </table>

                            <hr class="divider">

                            <table class="specs">
                                <thead>
                                    <tr>
                                        <th>Параметр</th>
                                        <th>Значение</th>
                                        <th>Примечание</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Кодек видео</td>
                                        <td>H.264</td>
                                        <td>Контейнер MP4, profile Main</td>
                                    </tr>
                                    <tr>
                                        <td>Частота кадров</td>
                                        <td>25 fps</td>
                                        <td>Фиксированная</td>
                                    </tr>
                                    <tr>
                                        <td>Цветовая модель</td>
                                        <td>RGB / sRGB</td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <td>Статика</td>
                                        <td>JPEG / PNG</td>
                                        <td>RGB, макс. 10 МБ</td>
                                    </tr>
                                    <tr>
                                        <td>Имя файла</td>
                                        <td>Только латиница, без пробелов</td>
                                        <td>brand_1920x1080_10s_v1.mp4</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div class="options">
                            <div class="option" :class="{ open: openOptions.opt1a }">
                                <div class="option-header" @click="toggleOption('opt1a')">
                                    <span class="option-tag tag-a">А</span> Креативы есть
                                    <svg class="option-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 6l4 4 4-4"/></svg>
                                </div>
                                <div class="option-body">
                                    <p>
                                        Убедитесь что имеются все креативы, всех размеров для будущей кампании — сверьтесь с таблицей и тех спецификациями выше.
                                        Загрузите креативы на платформу
                                        (<a :href="creativesCreateUrl">через форму загрузки</a>
                                        или откройте
                                        <a :href="creativesUrl">список креативов</a>,
                                        обязательно укажите понятное имя, для указания бренда — используйте выпадающее меню.
                                        (если бренда нет в списке — свяжитесь с нашим менеджером).
                                    </p>
                                </div>
                            </div>
                            <div class="option" :class="{ open: openOptions.opt1b }">
                                <div class="option-header" @click="toggleOption('opt1b')">
                                    <span class="option-tag tag-b">Б</span> Креативов нет
                                    <svg class="option-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 6l4 4 4-4"/></svg>
                                </div>
                                <div class="option-body">
                                    <p>
                                        Обратитесь на
                                        <a href="mailto:sales@2digital.tech">sales@2digital.tech</a>
                                        для оценки необходимых вариантов креативов и мы подготовим расчет.
                                        Учитывайте, что это может занять дополнительное время.
                                        Подробнее о работе в платформе — в разделе
                                        <a :href="helpUrl">Справка</a>.
                                    </p>
                                </div>
                            </div>
                            <div class="option" :class="{ open: openOptions.opt1c }">
                                <div class="option-header" @click="toggleOption('opt1c')">
                                    <span class="option-tag tag-c">В</span> Креативы есть частично
                                    <svg class="option-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 6l4 4 4-4"/></svg>
                                </div>
                                <div class="option-body">
                                    <p>
                                        Опишите, какие форматы у вас есть. Если каких-то форматов не хватает — мы поможем доработать необходимые размеры.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- STEP 2 -->
            <div class="step-wrapper">
                <div class="step step-2" :class="{ open: openSteps.step2 }">
                    <div class="step-header" @click="toggleStep('step2')">
                        <div class="step-num">2</div>
                        <div class="step-label">
                            <strong>Кампания</strong>
                            <span>Настройка и параметры размещения</span>
                        </div>
                        <svg class="chevron" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 7.5l5 5 5-5"/></svg>
                    </div>
                    <div class="step-body">
                        <div class="options">
                            <div class="option" :class="{ open: openOptions.opt2a }">
                                <div class="option-header" @click="toggleOption('opt2a')">
                                    <span class="option-tag tag-a">А</span> Знаю, какие экраны нужны
                                    <svg class="option-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 6l4 4 4-4"/></svg>
                                </div>
                                <div class="option-body">
                                    <p>
                                        Создайте кампанию по
                                        <a :href="createCampaignUrl">этой ссылке</a>
                                        (или через «+» напротив пункта «Кампании» в меню RTB).
                                        Для создания кампании введите её имя, выберите рекламодателя и бренд
                                        (если они отсутствуют в списке — обратитесь на
                                        <a href="mailto:sales@2digital.tech">sales@2digital.tech</a>).
                                        После этого задайте основные параметры кампании: срок, ограничение бюджета, настройки выходов по часам и дням.
                                    </p>
                                    <p>
                                        После этого отметьте загруженные вами креативы — это позволит автоматически отфильтровать доступные стороны
                                        в зависимости от имеющихся форматов. Финальный шаг — выбор необходимых экранов.
                                    </p>
                                    <p>
                                        После того как все параметры указаны, экраны и креативы выбраны — нажмите кнопку «Создать кампанию».
                                        Кампания будет отправлена на модерацию.
                                    </p>
                                </div>
                            </div>
                            <div class="option" :class="{ open: openOptions.opt2b }">
                                <div class="option-header" @click="toggleOption('opt2b')">
                                    <span class="option-tag tag-b">Б</span> Не знаю, какие экраны выбрать
                                    <svg class="option-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 6l4 4 4-4"/></svg>
                                </div>
                                <div class="option-body">
                                    <p>
                                        Если вы затрудняетесь с выбором сторон — есть несколько сформированных пакетов,
                                        которые позволят быстро построить охват по выбранным городам или по выбранным категориям локаций
                                        (аптеки, БЦ, ТРЦ). Пакеты представляют фиксированный набор экранов по фиксированной цене.
                                    </p>
                                    <p>
                                        Если всё-таки необходим детальный выбор сторон — обратитесь на
                                        <a href="mailto:sales@2digital.tech">sales@2digital.tech</a>
                                        или через раздел
                                        <a :href="helpUrl">Справка</a>
                                        или создайте пакетную кампанию на
                                        <a href="/rtb/package-campaigns/create">этой странице</a>.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- STEP 3 -->
            <div class="step-wrapper">
                <div class="step step-3" :class="{ open: openSteps.step3 }">
                    <div class="step-header" @click="toggleStep('step3')">
                        <div class="step-num">3</div>
                        <div class="step-label">
                            <strong>Запуск</strong>
                            <span>Модерация, старт и контроль кампании</span>
                        </div>
                        <svg class="chevron" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 7.5l5 5 5-5"/></svg>
                    </div>
                    <div class="step-body">
                        <div class="options">
                            <div class="option" :class="{ open: openOptions.opt3a }">
                                <div class="option-header" @click="toggleOption('opt3a')">
                                    <span class="option-tag tag-a">А</span> Всё готово — запускаю сам
                                    <svg class="option-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 6l4 4 4-4"/></svg>
                                </div>
                                <div class="option-body">
                                    <p>
                                        После нажатия кнопки «Создать кампанию» (если был выбран статус «Активна») —
                                        кампания автоматически помещается на модерацию. Если модерация пройдена — запуск следует в течение нескольких часов.
                                    </p>
                                    <p>
                                        Если кампания была создана со статусом «Приостановлена» — для её запуска необходимо перейти на экран
                                        <a :href="campaignsHomeUrl">«Кампании»</a>
                                        и нажать на кнопку запуска. В этом случае кампания будет помещена на модерацию и запущена в скором времени.
                                    </p>
                                </div>
                            </div>
                            <div class="option" :class="{ open: openOptions.opt3b }">
                                <div class="option-header" @click="toggleOption('opt3b')">
                                    <span class="option-tag tag-b">Б</span> Нужна помощь при запуске
                                    <svg class="option-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 6l4 4 4-4"/></svg>
                                </div>
                                <div class="option-body">
                                    <p>
                                        Обратитесь на
                                        <a href="mailto:sales@2digital.tech">sales@2digital.tech</a>
                                        — поможем решить возникшие вопросы.
                                        Краткая инструкция по платформе — в разделе
                                        <a :href="helpUrl">Справка</a>.
                                    </p>
                                </div>
                            </div>
                            <div class="option" :class="{ open: openOptions.opt3c }">
                                <div class="option-header" @click="toggleOption('opt3c')">
                                    <span class="option-tag tag-c">В</span> Кампания идёт — хочу что-то изменить
                                    <svg class="option-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 6l4 4 4-4"/></svg>
                                </div>
                                <div class="option-body">
                                    <p>
                                        Вы можете отредактировать текущую активную кампанию — изменив даты окончания,
                                        график выходов для оставшегося периода, а также скорректировать бюджетные параметры
                                        (в рамках оставшегося бюджета). После коррекции кампания будет приостановлена и помещена на модерацию.
                                        Кампания будет реактивирована после проверки нашим менеджером.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- STEP 4 -->
            <div class="step-wrapper">
                <div class="step step-4" :class="{ open: openSteps.step4 }">
                    <div class="step-header" @click="toggleStep('step4')">
                        <div class="step-num">4</div>
                        <div class="step-label">
                            <strong>Отчёты</strong>
                            <span>Аналитика выходов и аудиторные данные</span>
                        </div>
                        <svg class="chevron" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 7.5l5 5 5-5"/></svg>
                    </div>
                    <div class="step-body">
                        <div class="options">
                            <div class="option" :class="{ open: openOptions.opt4a }">
                                <div class="option-header" @click="toggleOption('opt4a')">
                                    <span class="option-tag tag-a">А</span> Хочу отчёт по выходам
                                    <svg class="option-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 6l4 4 4-4"/></svg>
                                </div>
                                <div class="option-body">
                                    <p>
                                        Вы можете просмотреть отчёт по прошедшему периоду на экране
                                        <a :href="campaignsHomeUrl">кампаний</a>,
                                        кликнув по кнопке «Статистика». Там же вы можете выгрузить детальный отчёт в Excel.
                                    </p>
                                </div>
                            </div>
                            <div class="option" :class="{ open: openOptions.opt4b }">
                                <div class="option-header" @click="toggleOption('opt4b')">
                                    <span class="option-tag tag-b">Б</span> Хочу аудиторные данные
                                    <svg class="option-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 6l4 4 4-4"/></svg>
                                </div>
                                <div class="option-body">
                                    <p>
                                        На создании RTB-кампании включите галку «Добавить аудиторные данные»:
                                        +10% к счёту, ставка на аукцион не меняется. Снять галку нельзя.
                                        В статистике появятся вкладки «Показы» и «Аудитория»:
                                        контакты у экрана (OTS) по 15-минутным интервалам.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'CampaignFlow',
    props: {
        userData: {
            type: Object,
            default: () => ({}),
        },
        campaignsHomeUrl: {
            type: String,
            required: true,
        },
        createCampaignUrl: {
            type: String,
            required: true,
        },
        creativesUrl: {
            type: String,
            required: true,
        },
        creativesCreateUrl: {
            type: String,
            required: true,
        },
        helpUrl: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            openSteps: {
                step1: false,
                step2: false,
                step3: false,
                step4: false,
            },
            openOptions: {
                opt1a: false,
                opt1b: false,
                opt1c: false,
                opt2a: false,
                opt2b: false,
                opt3a: false,
                opt3b: false,
                opt3c: false,
                opt4a: false,
                opt4b: false,
            },
            isDismissing: false,
            dismissError: '',
        };
    },
    methods: {
        toggleStep(id) {
            this.openSteps[id] = !this.openSteps[id];
        },
        toggleOption(id) {
            this.openOptions[id] = !this.openOptions[id];
        },
        async dismissFlow() {
            if (this.isDismissing) {
                return;
            }

            this.isDismissing = true;
            this.dismissError = '';

            const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

            try {
                const response = await fetch('/campaign-flow/dismiss', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'X-CSRF-TOKEN': csrfToken,
                    },
                    credentials: 'same-origin',
                    body: JSON.stringify({}),
                });

                const data = await response.json().catch(() => ({}));

                if (!response.ok) {
                    this.dismissError = data.message || 'Не удалось сохранить настройку. Попробуйте ещё раз.';
                    this.isDismissing = false;
                    return;
                }

                window.location.href = data.redirect_url || this.campaignsHomeUrl;
            } catch (error) {
                this.dismissError = 'Ошибка сети. Попробуйте ещё раз.';
                this.isDismissing = false;
            }
        },
    },
};
</script>
